import * as LDClient from 'launchdarkly-js-client-sdk';
import _, {isEmpty} from 'lodash';

import {isOnline} from '../../helpers/browser/networkReachabilityHelpers';
import {isDevelopment, isProduction, isStaging} from '../../helpers/environment/envHelpers';
import {CompanyModel} from '../../models/api/company/companyModel';
import {isGlobalAdmin} from '../../models/helpers/accessModelHelpers';
import {ActionBase} from '../../store/actionTypes';
import {CommonAppAction} from '../../store/commonAppAction';
import {CommonAppState} from '../../store/commonAppState';
import {CommonAppStore} from '../../store/commonAppStore';
import {updateFeatureFlags} from '../../store/featureFlags/featureFlagsActions';
import {reportToBugsnag} from '../bugsnag/bugsnag';
import {trackEvent, TrackingTypesEnum} from '../tracking/tracking';

/*
 * Constants.
 */

// For debugging.
const hasRuntimeFeatureFlagsInDevelopment = false;

/*
 * State.
 */

const requestedFlags = new Map<string, unknown>();
let appStore: CommonAppStore<CommonAppState, CommonAppAction | ActionBase> | undefined;
let runtimeClient: LDClient.LDClient | undefined;

/*
 * Setup.
 */

export function setupFeatureFlags<State extends CommonAppState>(
  store: CommonAppStore<State, CommonAppAction | ActionBase>,
) {
  appStore = store;
  const state = store.getState();
  // TODO: Move the config to the common data state.
  // @ts-expect-error Need to move config.
  const {isEnabled, flags} = state.data.config.launchdarkly;

  // Temporary instrumentation to troubleshoot https://frontjira.atlassian.net/browse/PB-12802
  if (isProduction() || isStaging()) {
    if (!isEnabled) {
      reportToBugsnag(new Error(`Feature flags disabled in boot data for teammate ${state.data.userId}`));
    } else if (!flags) {
      reportToBugsnag(new Error(`Feature flags missing in boot data for teammate ${state.data.userId}`));
    }
  }

  if (!isEnabled) {
    return;
  }

  // No need to wait for this to complete.
  setupRuntimeFeatureFlagsAsync(store);
}

export function reconnectRuntimeFeatureFlagsAsync() {
  // TODO: Re-enable this on mobile. And remove FF update action from refreshBootAction in dataActions.ts
  if (!appStore) {
    reportToBugsnag(new Error('Trying to reconnect runtime feature flags with no app store'));
    return;
  }
  setupRuntimeFeatureFlagsAsync(appStore);
}

async function setupRuntimeFeatureFlagsAsync<State extends CommonAppState>(
  store: CommonAppStore<State, CommonAppAction | ActionBase>,
) {
  // Runtime feature flags won't work while offline.
  if (!isOnline()) {
    return;
  }

  // Don't connect to LaunchDarkly in development.
  if (isDevelopment() && !hasRuntimeFeatureFlagsInDevelopment) {
    return;
  }

  const newRuntimeClient = createRuntimeClient(store.getState());

  // Co-locate the clean up of the old client with the assignment of the new client.
  if (runtimeClient) {
    runtimeClient.close();
  }

  runtimeClient = newRuntimeClient;

  try {
    await runtimeClient.waitForInitialization();
  } catch (error) {
    reportToBugsnag(error);
    return;
  }

  // Update the feature flags since we may have missed an update.
  const newFlags = runtimeClient.allFlags();
  store.dispatch(updateFeatureFlags(newFlags));

  runtimeClient.on('change', (changes: LDClient.LDFlagChangeset) => {
    const newFeatureFlags = _(changes)
      .toPairs()
      // Skip the initial call, where the change record only has a 'previous' prop.
      .filter(([, change]) => 'current' in change)
      // Extract the new value from the 'current' prop.
      .map(([name, change]) => [name, change.current])
      .fromPairs()
      .value();

    if (isEmpty(newFeatureFlags)) {
      return;
    }

    store.dispatch(updateFeatureFlags(newFeatureFlags));
  });
}

/*
 * API.
 */

export function trackFlagRequested(flag: string, flagValue: any) {
  if (requestedFlags.get(flag) === flagValue) {
    return;
  }

  requestedFlags.set(flag, flagValue);
  trackEvent(TrackingTypesEnum.FLAG_REQUESTED, flag, flagValue);
}

/*
 * Helpers.
 */

function createRuntimeClient(state: CommonAppState): LDClient.LDClient {
  // Initialize the LaunchDarkly client.

  // TODO: Move the config to the common data state.
  // @ts-expect-error Need to move config.
  const {flags, clientSideId, userHash} = state.data.config.launchdarkly;
  const {userId, teammates, company} = state.data;
  const user = teammates[userId];
  if (!user) {
    throw new Error('User not found.');
  }

  // See `teammateToLdUser` on the backend.
  const userContext: LDClient.LDSingleKindContext = {
    // Custom attributes.
    firstName: user.givenName,
    lastName: user.familyName,
    email: user.email,
    isAdmin: isGlobalAdmin(user.roleByNamespace),
    teammateCreatedAt: user.createdAt,
    ...makeCompanyAttributes(company),

    // Standard attributes.
    kind: 'user',
    anonymous: false,
    key: userId.toString(),
    name: undefined,
    _meta: {
      privateAttributes: ['firstName', 'lastName', 'email'],
    },
  };

  return LDClient.initialize(clientSideId, userContext, {
    hash: userHash,
    bootstrap: flags,
    fetchGoals: false,
  });
}

// See `getAttributesFromCompany` on the backend.
function makeCompanyAttributes(company: CompanyModel) {
  return {
    companyId: company.id,
    companySize: company.preferences.companySize,
    companyCreatedAt: company.createdAt,
    companyFutureCell: company.futureCell,
    companyPlan: company.plan,
    // For testing purposes. Matches what Ghost Inspector uses.
    isTestCompany: /^frontTestCompany-/.test(company.name),
  };
}
